import React, { useState, useMemo, useEffect } from "react";
import { X, MapPin, Sparkles, ChevronDown, ChevronUp, Code, Copy, Check } from "lucide-react";
import { LISTINGS, getStateName } from "../../constants";

const empty = {
  name: "",
  city: "",
  state: "al",
  rating: 4.5,
  reviewCount: 0,
  address: "",
  phone: "",
  website: "",
  priceRange: "$8 - $14 per round",
  description: "",
  image: "",
  featured: false,
  seoTitle: "",
  seoDescription: "",
  customSchemaType: "SportsActivityLocation",
  customJsonLd: "",
};

const CourseForm = ({ course, states, allCourses = [], onSave, onClose }) => {
  const [form, setForm] = useState(course ? { ...empty, ...course } : { ...empty });
  const [showSeoSection, setShowSeoSection] = useState(false);
  const [copiedSchema, setCopiedSchema] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Compute available cities for the currently selected state
  const stateCities = useMemo(() => {
    const selectedStateCode = (form.state || "al").toLowerCase();
    const citySet = new Set();

    // 1. From allCourses in dashboard
    (allCourses || []).forEach((c) => {
      if ((c.state || "").toLowerCase() === selectedStateCode && c.city && c.city.trim()) {
        citySet.add(c.city.trim());
      }
    });

    // 2. From LISTINGS in listingsData.json
    (LISTINGS || []).forEach((l) => {
      if ((l.stateCode || "").toLowerCase() === selectedStateCode && l.cityName && l.cityName.trim()) {
        citySet.add(l.cityName.trim());
      }
    });

    return Array.from(citySet).sort((a, b) => a.localeCompare(b));
  }, [form.state, allCourses]);

  // Determine if city is custom (not in stateCities list)
  const [isCustomCity, setIsCustomCity] = useState(() => {
    if (!course?.city) return false;
    return false;
  });

  // When state changes on a new course, default to first city if available
  useEffect(() => {
    if (!course && stateCities.length > 0 && !isCustomCity) {
      if (!form.city || !stateCities.includes(form.city)) {
        set("city", stateCities[0]);
      }
    }
  }, [form.state, stateCities, course, isCustomCity, form.city]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.city.trim()) {
      alert("Please select or enter a city");
      return;
    }
    onSave({
      ...form,
      city: form.city.trim(),
      rating: Math.min(5, Math.max(0, parseFloat(form.rating) || 0)),
      reviewCount: parseInt(form.reviewCount, 10) || 0,
      hours: form.hours || {
        Monday: "10:00 AM - 9:00 PM",
        Tuesday: "10:00 AM - 9:00 PM",
        Wednesday: "10:00 AM - 9:00 PM",
        Thursday: "10:00 AM - 9:00 PM",
        Friday: "10:00 AM - 11:00 PM",
        Saturday: "9:00 AM - 11:00 PM",
        Sunday: "9:00 AM - 8:00 PM",
      },
    });
  };

  const input =
    "w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="course-form-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-lg font-extrabold text-gray-900">
            {course ? "Edit Course" : "Add New Course"}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Course Name *</label>
            <input required data-testid="course-form-name" className={input} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Pirate's Island Adventure Golf" />
          </div>

          {/* State and City Selection */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">State *</label>
              <select
                data-testid="course-form-state"
                className={input}
                value={form.state}
                onChange={(e) => {
                  const nextState = e.target.value;
                  set("state", nextState);
                }}
              >
                {states.map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.name} ({s.code.toUpperCase()})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-sm font-medium text-gray-700">City *</label>
                {isCustomCity ? (
                  <button
                    type="button"
                    onClick={() => {
                      setIsCustomCity(false);
                      if (stateCities.length > 0) set("city", stateCities[0]);
                    }}
                    className="text-xs text-emerald-600 hover:text-emerald-700 font-semibold"
                  >
                    Select from list
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setIsCustomCity(true);
                      set("city", "");
                    }}
                    className="text-xs text-emerald-600 hover:text-emerald-700 font-semibold"
                  >
                    + Enter New City
                  </button>
                )}
              </div>

              {isCustomCity ? (
                <input
                  required
                  type="text"
                  data-testid="course-form-city-input"
                  className={input}
                  value={form.city}
                  placeholder="Type city name..."
                  onChange={(e) => set("city", e.target.value)}
                  autoFocus
                />
              ) : (
                <select
                  required
                  data-testid="course-form-city-select"
                  className={input}
                  value={form.city}
                  onChange={(e) => {
                    if (e.target.value === "__new__") {
                      setIsCustomCity(true);
                      set("city", "");
                    } else {
                      set("city", e.target.value);
                    }
                  }}
                >
                  <option value="">-- Select City ({stateCities.length} available) --</option>
                  {stateCities.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                  {course?.city && !stateCities.includes(course.city) && (
                    <option value={course.city}>{course.city} (Current)</option>
                  )}
                  <option value="__new__">+ Enter New / Other City...</option>
                </select>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Rating (0-5)</label>
              <input
                type="number" step="0.1" min="0" max="5"
                className={input}
                value={form.rating}
                onChange={(e) => set("rating", e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Review Count</label>
              <input
                type="number" min="0"
                className={input}
                value={form.reviewCount}
                onChange={(e) => set("reviewCount", e.target.value)}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Address</label>
            <input className={input} value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="123 Fairway Ln" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone</label>
              <input className={input} value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(555) 123-4567" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Price Range</label>
              <input className={input} value={form.priceRange} onChange={(e) => set("priceRange", e.target.value)} placeholder="$8 - $14 per round" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Website URL</label>
            <input className={input} value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="https://..." />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Image URL</label>
            <input className={input} value={form.image} onChange={(e) => set("image", e.target.value)} placeholder="https://... (leave blank for default image)" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-sm font-medium text-gray-700">Description</label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    const current = form.description || "";
                    set("description", current ? `${current} [Visit Official Website](https://example.com)` : `[Visit Official Website](https://example.com)`);
                  }}
                  className="text-xs text-blue-600 hover:text-blue-700 font-semibold"
                >
                  🔗 + Add Link
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const stateCode = (form.state || "al").toUpperCase();
                    const desc = `Find ${
                      form.name || "this mini golf venue"
                    }, an exciting mini golf course located in ${
                      form.city || "the area"
                    }, ${stateCode}. The course features a ${
                      form.rating || 4.5
                    }-star rating from ${
                      form.reviewCount || 0
                    } reviews, offering creative obstacles, family-friendly putt-putt challenges, and memorable entertainment for players of all ages.`;
                    set("description", desc);
                  }}
                  className="text-xs text-emerald-600 hover:text-emerald-700 font-semibold"
                >
                  ✨ Generate Description
                </button>
              </div>
            </div>
            <textarea
              rows={4}
              data-testid="course-form-description"
              className={input}
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              placeholder="Describe course features, attractions... (e.g. Visit [Official Website](https://...) or https://...)"
            />
            <p className="text-[11px] text-gray-500 mt-1 flex items-center justify-between">
              <span>💡 <strong>Hyperlink Support:</strong> Write <code>[Link Text](https://url.com)</code> or paste raw URLs <code>https://...</code> to display clickable links.</span>
            </p>
          </div>

          {/* Collapsible Schema & SEO Overrides */}
          <div className="border border-emerald-100 bg-emerald-50/40 rounded-xl p-4">
            <button
              type="button"
              onClick={() => setShowSeoSection(!showSeoSection)}
              className="w-full flex items-center justify-between text-left"
            >
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-emerald-600" />
                <span className="text-sm font-bold text-gray-900">
                  Schema.org & SEO Rich Snippets Options
                </span>
                <span className="text-[11px] font-medium text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
                  Customizable
                </span>
              </div>
              {showSeoSection ? (
                <ChevronUp className="w-4 h-4 text-gray-500" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-500" />
              )}
            </button>

            {showSeoSection && (
              <div className="mt-4 pt-4 border-t border-emerald-100 space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Custom Meta Title (Optional Override)
                  </label>
                  <input
                    type="text"
                    className={input}
                    value={form.seoTitle || ""}
                    onChange={(e) => set("seoTitle", e.target.value)}
                    placeholder={`e.g. ${form.name || "Course Name"} - Top Rated Mini Golf in ${form.city || "City"}`}
                  />
                  <p className="text-[11px] text-gray-400 mt-1">
                    Leave blank to auto-generate from course name, city, and state.
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Custom Meta Description (Optional Override)
                  </label>
                  <textarea
                    rows={2}
                    className={input}
                    value={form.seoDescription || ""}
                    onChange={(e) => set("seoDescription", e.target.value)}
                    placeholder="Leave blank to use the cleaned course description."
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Primary Schema.org @type
                  </label>
                  <select
                    className={input}
                    value={form.customSchemaType || "SportsActivityLocation"}
                    onChange={(e) => set("customSchemaType", e.target.value)}
                  >
                    <option value="SportsActivityLocation">SportsActivityLocation & LocalBusiness (Standard)</option>
                    <option value="AmusementPark">AmusementPark</option>
                    <option value="LocalBusiness">LocalBusiness</option>
                    <option value="EntertainmentBusiness">EntertainmentBusiness</option>
                  </select>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-xs font-semibold text-gray-700">
                      Custom Raw JSON-LD Schema (Optional)
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        const previewSchema = {
                          "@context": "https://schema.org",
                          "@type": form.customSchemaType || "SportsActivityLocation",
                          name: form.name || "Course Name",
                          image: form.image || "https://minigolfalabama.com/logo.png",
                          description: form.seoDescription || form.description || undefined,
                          telephone: form.phone || undefined,
                          url: form.website || undefined,
                          priceRange: form.priceRange || "$$",
                          address: {
                            "@type": "PostalAddress",
                            streetAddress: form.address || undefined,
                            addressLocality: form.city || undefined,
                            addressRegion: (form.state || "al").toUpperCase(),
                            addressCountry: "US",
                          },
                          aggregateRating: {
                            "@type": "AggregateRating",
                            ratingValue: parseFloat(form.rating) || 4.5,
                            reviewCount: parseInt(form.reviewCount, 10) || 0,
                            bestRating: 5,
                            worstRating: 1,
                          },
                        };
                        set("customJsonLd", JSON.stringify(previewSchema, null, 2));
                      }}
                      className="text-xs text-emerald-600 hover:text-emerald-700 font-semibold"
                    >
                      ✨ Auto-Fill Course Schema
                    </button>
                  </div>
                  <textarea
                    rows={4}
                    className={`${input} font-mono text-xs`}
                    value={form.customJsonLd || ""}
                    onChange={(e) => set("customJsonLd", e.target.value)}
                    placeholder='{"@context": "https://schema.org", "@type": "SportsActivityLocation", ...}'
                  />
                </div>
              </div>
            )}
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.featured}
              onChange={(e) => set("featured", e.target.checked)}
              className="w-4 h-4 accent-emerald-500 rounded"
            />
            <span className="text-sm font-medium text-gray-700">Mark as Featured Course</span>
          </label>
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="course-form-submit"
              className="px-5 py-2.5 text-sm font-semibold text-white bg-emerald-500 rounded-lg hover:bg-emerald-600 transition-colors shadow-sm"
            >
              {course ? "Save Changes" : "Create Course"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CourseForm;
